/**
 * Flow map live CSS (FM_LIVE_CSS): everything only the interactive component
 * draws. The canvas wrapper and React Flow's theme, handles, arrows and their
 * labels, the group resizer, the inspector panel and its fields, the add menu,
 * the toolbar extras and the interactive table's sort icons. The shared look
 * (cards, buttons, the view switch, the table) is FM_CSS in css.ts; this sheet
 * only adds to it, and <FlowMap> injects both.
 *
 * React Flow is themed through its own CSS variables, scoped to our canvas and
 * mapped to the host's colour tokens, so it follows light and dark mode on every
 * host by itself; React Flow's colorMode stays "light" (see FlowMapCanvas).
 * React Flow's structural sheet (xyflowCss.ts) sits in `@layer base`, so every
 * unlayered rule here wins over it without extra specificity.
 */
import { FM_LABEL_INK, FM_SR, FM_TOKEN as T, FM_TONE_COLOR, fmMix } from "./css.js";
import { kindTone } from "./table.js";
import { FLOW_NODE_KINDS, FLOW_TONES } from "./types.js";

const HAIR = fmMix(T.fg, 12);
const EDGE = fmMix(T.fg, 35);
const FIELD_BORDER = fmMix(T.fg, 18);
const RING = `0 0 0 3px ${fmMix(T.ring, 28)}`;
const PANEL_SHADOW = `0 1px 2px ${fmMix(T.fg, 8)},0 12px 32px ${fmMix(T.fg, 14)}`;
const MENU_SHADOW = `0 2px 6px ${fmMix(T.fg, 8)},0 12px 28px ${fmMix(T.fg, 16)}`;

const MENU_KINDS = FLOW_NODE_KINDS.map((k) => `.wa-fm__menu-item--${k}{--wa-fm-tone:${FM_TONE_COLOR[kindTone(k)]};}`).join("\n");
const TONE_DOTS = FLOW_TONES.map((t) => `.wa-fm__tone--${t}{--wa-fm-tone:${FM_TONE_COLOR[t]};}`).join("\n");

const XY_THEME = [
  "--xy-background-color:transparent",
  `--xy-node-color:${T.fg}`,
  "--xy-node-background-color:transparent",
  "--xy-node-border:0 none",
  "--xy-node-border-selected:0 none",
  "--xy-node-border-radius:12px",
  "--xy-node-boxshadow-hover:none",
  "--xy-node-boxshadow-selected:none",
  "--xy-node-group-background-color:transparent",
  `--xy-edge-stroke:${EDGE}`,
  "--xy-edge-stroke-width:1.5",
  `--xy-edge-stroke-selected:${T.ring}`,
  `--xy-connectionline-stroke:${T.ring}`,
  "--xy-connectionline-stroke-width:1.5",
  `--xy-handle-background-color:${T.primary}`,
  `--xy-handle-border-color:${T.card}`,
  `--xy-selection-background-color:${fmMix(T.primary, 8)}`,
  `--xy-selection-border:1px dashed ${fmMix(T.primary, 60)}`,
  `--xy-minimap-background-color:${T.card}`,
  `--xy-minimap-mask-background-color:${fmMix(T.muted, 60)}`,
  `--xy-minimap-mask-stroke-color:${fmMix(T.ring, 50)}`,
  "--xy-minimap-mask-stroke-width:1",
  `--xy-minimap-node-background-color:${fmMix(T.fg, 14)}`,
  "--xy-minimap-node-stroke-color:transparent",
  "--xy-minimap-node-stroke-width:0",
  `--xy-edge-label-background-color:${T.card}`,
  `--xy-edge-label-color:${T.mutedFg}`,
  `--xy-resize-background-color:${T.ring}`,
  `--xy-background-pattern-color:${fmMix(T.fg, 16)}`,
].join(";");

export const FM_LIVE_CSS = `
.wa-fm--live:focus{outline:none;}
.wa-fm__btn-text{white-space:nowrap;}
.wa-fm__seg{box-sizing:border-box;display:inline-flex;align-items:center;gap:2px;height:32px;padding:0 2px 0 9px;border:1px solid ${HAIR};border-radius:8px;background:${T.card};color:${T.mutedFg};}
.wa-fm__seg>svg{flex:none;margin-right:3px;}
.wa-fm__seg>.wa-fm__btn{width:26px;min-width:26px;height:26px;padding:0;border-color:transparent;border-radius:6px;background:transparent;}
.wa-fm__menu-wrap{position:relative;display:inline-flex;}
.wa-fm__menu{position:absolute;top:calc(100% + 6px);left:0;z-index:30;box-sizing:border-box;display:grid;gap:1px;min-width:196px;padding:4px;background:${T.card};color:${T.fg};border:1px solid ${HAIR};border-radius:10px;box-shadow:${MENU_SHADOW};}
.wa-fm__menu-item{--wa-fm-tone:${T.mutedFg};box-sizing:border-box;display:flex;align-items:center;gap:9px;width:100%;height:34px;margin:0;padding:0 10px;border:0;border-radius:7px;background:transparent;color:inherit;font-family:inherit;font-size:13px;font-weight:500;line-height:1;text-align:left;cursor:pointer;}
.wa-fm__menu-item svg{flex:none;color:var(--wa-fm-tone);}
.wa-fm__menu-item:hover,.wa-fm__menu-item:focus-visible{background:${fmMix(T.fg, 6)};outline:none;}
${MENU_KINDS}
.wa-fm__body{position:relative;min-width:0;}
.wa-fm__main{min-width:0;}
.wa-fm__body--table.has-inspector{display:grid;grid-template-columns:minmax(0,1fr) 300px;gap:12px;align-items:start;}
.wa-fm__canvas{position:absolute;inset:0;}
.wa-fm__canvas.is-page-scroll .react-flow__pane{touch-action:pan-x pan-y;}
.wa-fm__defs{position:absolute;width:0;height:0;overflow:hidden;pointer-events:none;}
.wa-fm__hint{position:absolute;left:12px;top:10px;z-index:5;box-sizing:border-box;max-width:min(440px,calc(100% - 24px));padding:5px 10px;border-radius:8px;background:${fmMix(T.card, 88)};color:${T.mutedFg};font-size:12px;line-height:1.4;pointer-events:none;}
.wa-fm__empty--live{z-index:4;pointer-events:none;}
.wa-fm__empty-title{margin-bottom:4px;color:${T.fg};font-size:15px;font-weight:650;}
.wa-fm__canvas .react-flow{${XY_THEME};color:${T.fg};font-family:inherit;}
.wa-fm__canvas .react-flow__node-group{padding:0;border:0;border-radius:16px;background:none;}
.wa-fm__canvas .react-flow__node:focus{outline:none;}
.wa-fm__canvas .react-flow__node:focus-visible>.wa-fm-node{outline:2px solid ${T.ring};outline-offset:3px;}
.wa-fm__canvas .react-flow__handle.wa-fm__handle{width:10px;height:10px;min-width:0;min-height:0;border:2px solid ${T.card};border-radius:999px;background:${T.primary};opacity:0;transition:opacity .15s ease;}
.wa-fm__canvas .react-flow__handle.wa-fm__handle::after{content:"";position:absolute;inset:-7px;border-radius:999px;}
.wa-fm__canvas.is-editable .react-flow__node:hover .wa-fm__handle,.wa-fm__canvas.is-editable .react-flow__node.selected .wa-fm__handle,.wa-fm__canvas.is-editable .react-flow__node:focus-visible .wa-fm__handle,.wa-fm__canvas.is-connecting .wa-fm__handle{opacity:1;}
.wa-fm__canvas .react-flow__handle.wa-fm__handle.connectingto,.wa-fm__canvas .react-flow__handle.wa-fm__handle.valid{background:${T.success};}
.wa-fm__canvas:not(.is-editable) .react-flow__handle{pointer-events:none;}
.wa-fm__canvas .react-flow__edge-path.wa-fm-edge{stroke:${EDGE};stroke-width:1.5;stroke-linecap:round;}
.wa-fm__canvas .react-flow__edge-path.wa-fm-edge--yes{stroke:${T.success};}
.wa-fm__canvas .react-flow__edge-path.wa-fm-edge--no{stroke:${T.danger};}
.wa-fm__canvas .react-flow__edge-path.wa-fm-edge--optional{stroke-dasharray:6 5;}
.wa-fm__canvas .react-flow__edge:hover .react-flow__edge-path.wa-fm-edge{stroke-width:2.25;}
.wa-fm__canvas .react-flow__edge-path.wa-fm-edge.is-selected,.wa-fm__canvas .react-flow__edge:focus-visible .react-flow__edge-path.wa-fm-edge{stroke:${T.ring};stroke-width:2.25;}
.wa-fm__canvas .react-flow__edge:focus-visible{outline:none;}
.wa-fm-edge-marker{fill:${EDGE};stroke:${EDGE};stroke-width:1;}
.wa-fm-edge-marker--yes{fill:${T.success};stroke:${T.success};}
.wa-fm-edge-marker--no{fill:${T.danger};stroke:${T.danger};}
.wa-fm-edge-marker--selected{fill:${T.ring};stroke:${T.ring};}
.wa-fm__edge-label{position:absolute;top:0;left:0;box-sizing:border-box;max-width:180px;padding:2px 8px;border:1px solid ${HAIR};border-radius:999px;background:${T.card};color:${T.mutedFg};font-size:11.5px;font-weight:600;line-height:16px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;pointer-events:all;cursor:pointer;}
.wa-fm__edge-label--yes{color:${FM_LABEL_INK.yes};border-color:${fmMix(T.success, 40)};background:${fmMix(T.success, 10, T.card)};}
.wa-fm__edge-label--no{color:${FM_LABEL_INK.no};border-color:${fmMix(T.danger, 40)};background:${fmMix(T.danger, 10, T.card)};}
.wa-fm__edge-label.is-selected{border-color:${T.ring};box-shadow:${RING};}
.wa-fm__canvas .react-flow__resize-control.handle{width:9px;height:9px;border:1.5px solid ${T.ring};border-radius:3px;background:${T.card};}
.wa-fm__canvas .react-flow__resize-control.line{border-color:${fmMix(T.ring, 55)};}
.wa-fm__canvas .react-flow__minimap{overflow:hidden;border:1px solid ${HAIR};border-radius:10px;box-shadow:0 1px 3px ${fmMix(T.fg, 10)};}
.wa-fm__link-text{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.wa-fm__inspector{box-sizing:border-box;display:flex;flex-direction:column;min-width:0;min-height:0;background:${T.card};color:${T.fg};border:1px solid ${HAIR};border-radius:12px;box-shadow:${PANEL_SHADOW};overflow:hidden;z-index:6;}
.wa-fm__body--canvas>.wa-fm__inspector{position:absolute;top:10px;right:10px;bottom:10px;width:300px;max-width:calc(100% - 20px);}
.wa-fm__body--table>.wa-fm__inspector{position:sticky;top:12px;max-height:var(--wa-fm-h,560px);}
.wa-fm__inspector-head{display:flex;align-items:center;gap:4px;min-height:44px;padding:6px 8px 6px 14px;border-bottom:1px solid ${HAIR};}
.wa-fm__inspector-title{flex:1 1 auto;min-width:0;margin:0;color:${T.mutedFg};font-size:11px;font-weight:700;letter-spacing:.07em;line-height:1.2;text-transform:uppercase;}
.wa-fm__inspector-body{flex:1 1 auto;min-height:0;overflow:auto;overscroll-behavior:contain;display:grid;align-content:start;gap:16px;padding:14px 14px 18px;}
.wa-fm__section{display:grid;gap:8px;min-width:0;margin:0;padding:0;border:0;}
.wa-fm__section-title{display:flex;align-items:center;gap:8px;margin:0;color:${T.mutedFg};font-size:11px;font-weight:700;letter-spacing:.07em;line-height:1.3;text-transform:uppercase;}
.wa-fm__count{margin-left:auto;color:${T.mutedFg};font-size:12px;font-weight:600;letter-spacing:0;text-transform:none;font-variant-numeric:tabular-nums;}
.wa-fm__field{display:grid;gap:5px;min-width:0;}
.wa-fm__label{color:${T.mutedFg};font-size:12px;font-weight:600;line-height:1.3;}
.wa-fm__input,.wa-fm__textarea{box-sizing:border-box;display:block;width:100%;min-width:0;height:32px;margin:0;padding:0 9px;border:1px solid ${FIELD_BORDER};border-radius:8px;background:${T.card};color:${T.fg};font-family:inherit;font-size:13px;line-height:1.3;}
.wa-fm__textarea{height:auto;min-height:76px;padding:7px 9px;line-height:1.45;resize:vertical;}
.wa-fm__input::placeholder,.wa-fm__textarea::placeholder{color:${T.mutedFg};opacity:.8;}
.wa-fm__input:focus,.wa-fm__textarea:focus{outline:none;border-color:${T.ring};box-shadow:${RING};}
.wa-fm__input[aria-invalid="true"]{border-color:${T.danger};}
.wa-fm__error{margin:0;color:${T.danger};font-size:12px;line-height:1.4;}
.wa-fm__rows{display:grid;gap:6px;margin:0;padding:0;list-style:none;}
.wa-fm__row{display:flex;align-items:center;gap:6px;min-width:0;}
.wa-fm__row>.wa-fm__input{flex:1 1 0;width:auto;}
.wa-fm__pickw{--wa-cmb-r:8px;width:100%;}
.wa-fm__row>.wa-fm__pickw{flex:1 1 0;width:auto;}
.wa-fm__row>.wa-fm__pickw--fit{flex:0 1 auto;max-width:46%;}
.wa-fm .wa-cmb-btn.wa-fm__pick{min-height:32px;height:32px;gap:6px;padding:0 6px 0 9px;border:1px solid ${FIELD_BORDER};background:${T.card};color:${T.fg};font-family:inherit;font-size:13px;line-height:1.3;}
.wa-fm .wa-cmb>.wa-cmb-btn.wa-fm__pick:focus-visible{outline:none;border-color:${T.ring};box-shadow:${RING};}
.wa-fm .wa-cmb-btn.wa-fm__pick .wa-cmb-chev{width:14px;height:14px;}
.wa-fm__pickw .wa-cmb-panel{font-size:13px;}
.wa-fm__pickw .wa-cmb-opt{padding:6px 8px;font-size:13px;}
.wa-fm__row--fields{align-items:flex-start;gap:10px;}
.wa-fm__row--fields>.wa-fm__field{flex:1 1 0;}
.wa-fm__row-label{flex:none;min-width:40px;color:${T.mutedFg};font-size:12px;font-weight:600;}
.wa-fm__card{display:grid;gap:6px;min-width:0;margin:0;padding:8px;border:1px solid ${HAIR};border-radius:10px;background:${fmMix(T.fg, 2, T.card)};}
.wa-fm__check{flex:none;width:16px;height:16px;margin:0;accent-color:${T.success};cursor:pointer;}
.wa-fm__check:disabled{opacity:1;cursor:default;}
.wa-fm__btn--sm{height:28px;min-width:28px;padding:0 9px;font-size:12px;}
.wa-fm__btn--sm.wa-fm__btn--icon{width:28px;padding:0;}
.wa-fm__btn--block{width:100%;}
.wa-fm__tones{display:flex;flex-wrap:wrap;gap:4px;}
.wa-fm__tone{--wa-fm-tone:${T.mutedFg};position:relative;display:inline-grid;place-items:center;width:30px;height:30px;border-radius:999px;cursor:pointer;}
.wa-fm__tone-dot{width:18px;height:18px;border-radius:999px;background:var(--wa-fm-tone);box-shadow:inset 0 0 0 1px ${fmMix(T.fg, 16)};transition:box-shadow .15s ease;}
.wa-fm__tone>input:checked+.wa-fm__tone-dot{box-shadow:0 0 0 2px ${T.card},0 0 0 4px var(--wa-fm-tone);}
.wa-fm__tone>input:focus-visible+.wa-fm__tone-dot{outline:2px solid ${T.ring};outline-offset:5px;}
${TONE_DOTS}
.wa-fm__details-title{margin:0;font-size:16px;font-weight:700;line-height:1.3;overflow-wrap:anywhere;}
.wa-fm__details-desc{margin:0;font-size:13.5px;line-height:1.55;white-space:pre-line;overflow-wrap:anywhere;}
.wa-fm__checklist{display:grid;gap:6px;margin:0;padding:0;list-style:none;font-size:13px;line-height:1.4;}
.wa-fm__checklist>li{margin:0;padding:0;}
.wa-fm__checklist label{display:flex;align-items:flex-start;gap:8px;cursor:default;}
.wa-fm__checklist .wa-fm__check{margin-top:1px;}
.wa-fm__checklist .is-done span{color:${T.mutedFg};text-decoration:line-through;}
.wa-fm__links{display:flex;flex-wrap:wrap;gap:6px;}
.wa-fm__jump{box-sizing:border-box;display:flex;align-items:center;gap:8px;width:100%;min-height:32px;margin:0;padding:5px 9px;border:1px solid ${HAIR};border-radius:8px;background:${T.card};color:${T.fg};font-family:inherit;font-size:13px;line-height:1.35;text-align:left;cursor:pointer;}
.wa-fm__jump:hover{background:${fmMix(T.fg, 4, T.card)};}
.wa-fm__jump:focus-visible{outline:2px solid ${T.ring};outline-offset:1px;}
.wa-fm__jump>svg{flex:none;color:${T.mutedFg};}
.wa-fm__jump-text{flex:1 1 auto;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.wa-fm__flip{transform:scaleX(-1);}
.wa-fm-table__sort svg{opacity:.45;}
.wa-fm-table th[aria-sort] .wa-fm-table__sort{color:${T.fg};}
.wa-fm-table th[aria-sort] .wa-fm-table__sort svg{opacity:1;}
@container (max-width:720px){
.wa-fm__body--canvas>.wa-fm__inspector,.wa-fm__body--table>.wa-fm__inspector{position:static;width:auto;max-width:none;max-height:none;margin-top:10px;}
.wa-fm__body--table.has-inspector{display:block;}
.wa-fm__hint{display:none;}
}
@container (max-width:560px){
.wa-fm__btn-text{${FM_SR}}
.wa-fm__menu-wrap>.wa-fm__btn{width:32px;padding:0;}
}
@media (prefers-reduced-motion:reduce){.wa-fm__canvas .react-flow__handle.wa-fm__handle,.wa-fm__tone-dot{transition:none;}}
`;
